/**
 * Navigation Interceptor Component
 * 
 * Watches AI messages for navigation commands and automatically navigates the user.
 * Used for the onboarding agent "training camp buddy" experience.
 * 
 * Navigation commands format: [NAVIGATE_TO:destination]
 * Examples:
 * - [NAVIGATE_TO:bookings]
 * - [NAVIGATE_TO:catalog]
 * - [NAVIGATE_TO:knowledge-base]
 * - [NAVIGATE_TO:reminders]
 */

import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export const NavigationInterceptor = ({ messages, onNavigationComplete, isActive = true }) => {
  const navigate = useNavigate();
  const lastProcessedIndex = useRef(-1);
  const isNavigating = useRef(false);
  
  useEffect(() => {
    if (!isActive || !messages || messages.length === 0) return;
    
    // Only process new messages
    if (messages.length <= lastProcessedIndex.current) return;
    
    // Get the last message
    const lastMessage = messages[messages.length - 1];
    const messageIndex = messages.length - 1;
    
    // Skip if we've already processed this message
    if (messageIndex === lastProcessedIndex.current) return;
    
    // Only process assistant messages
    if (!lastMessage || (lastMessage.role !== 'assistant' && lastMessage.type !== 'assistant')) {
      lastProcessedIndex.current = messageIndex;
      return;
    }
    
    // Get text content
    const text = lastMessage.text || lastMessage.content || '';
    
    // Look for navigation commands
    const navMatch = text.match(/\[NAVIGATE_TO:([^\]]+)\]/);
    
    if (navMatch && !isNavigating.current) {
      const destination = navMatch[1].trim();
      
      console.log('🧭 Onboarding agent navigating to:', destination);
      
      // Mark as navigating to prevent duplicate navigation
      isNavigating.current = true;
      
      // Smooth scroll to top first
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Navigate after a brief moment for smooth transition
      setTimeout(() => {
        navigate(`/${destination}`);
        
        // Call completion callback
        if (onNavigationComplete) {
          onNavigationComplete(destination);
        }
        
        // Reset navigation lock after a delay
        setTimeout(() => {
          isNavigating.current = false;
        }, 1000);
      }, 500);
      
      // Mark this message as processed
      lastProcessedIndex.current = messageIndex;
    } else {
      // No navigation command, just mark as processed
      lastProcessedIndex.current = messageIndex;
    }
  }, [messages, navigate, onNavigationComplete, isActive]);
  
  // This is a utility component, doesn't render anything
  return null;
};

// Helper function to clean navigation commands from displayed text
export const cleanNavigationCommands = (text) => {
  if (!text) return text;
  return text.replace(/\[NAVIGATE_TO:[^\]]+\]/g, '').trim();
};

export default NavigationInterceptor;

